import React from "react";
import { Button, Card, CardBody, Row, Col } from "reactstrap";
import { connect } from "react-redux";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import RowActions from "components/Table/RowActions";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import SocketEngine from "../../core/SocketEngine";
import loadingAction from "../../core/redux/actions/loadingAction";
import strings from "../../core/translate";
import Constants from "../../core/Constants";
import { apiErrorMessage } from "../../shared/api-error";
import { formatMileage } from "../../shared/mileage";
import SelectRow from "../../components/Select/SelectRow";
import PageHeader from "../../components/Page/PageHeader";
import EmptyState from "../../components/Page/EmptyState";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import { FormModal } from "../../components/Form/FormKit";
import JobCardView from "./JobCardView";
import { ConfirmModal } from "../work-orders/WorkOrderDialogs";
import "../../assets/css/job-cards.css";

/**
 * The job cards of one garage: what came in, on which vehicle, for whom and
 * where it stands. Opening one works it as the app does — see JobCardView.
 */
class JobCardList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      dataSource: [],
      merchants: [],
      branches: [],
      selectedMerchant: "",
      selectedBranch: "",
      branchName: "",
      garageScoped: false,
      viewing: null,
      // the closed-card history, paged on the server: only the page on
      // screen is ever in hand, so react-table is driven manually
      historyOpen: false,
      historyRows: [],
      historyLoading: false,
      historyPage: 0,
      historyPageSize: 10,
      historyHasNext: false,
      // set while the detail dialog was reached from the history, so
      // closing it goes back there rather than to the working list
      viewingFromHistory: false,
      // the row whose invoice is being settled, and whether the call is out
      payingRow: null,
      settling: false,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");
    const routeState = this.props.location.state || {};
    const { selectedBranch } = this.props.match.params;

    // reached from a garage record: this page is about that garage only
    if (selectedBranch && selectedBranch !== ":selectedBranch") {
      this.setState(
        {
          selectedBranch,
          garageScoped: true,
          branchName: routeState.branchName || "",
          selectedMerchant:
            merchantIdFromStorage || routeState.selectedMerchant || "",
        },
        this.callPage
      );
      if (routeState.openJobCardId) {
        this.openJobCard(selectedBranch, routeState.openJobCardId);
      }
      return;
    }

    if (merchantIdFromStorage) {
      this.setState({ selectedMerchant: merchantIdFromStorage }, this.fetchBranches);
    } else {
      this.fetchMerchants();
    }

    SocketEngine.on("job_card:updated", this.handleJobCardUpdated);
  }

  componentWillUnmount() {
    SocketEngine.off("job_card:updated", this.handleJobCardUpdated);
  }

  // A garage opened or advanced a job card in the app — reflect it here
  // without the admin reloading the page.
  handleJobCardUpdated = (payload) => {
    if (!this.state.selectedBranch) return;
    if (payload && payload.branchId && payload.branchId !== this.state.selectedBranch) {
      return;
    }
    this.callPage();
  };

  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      const merchants = response.data.items.map((merchant) => ({
        value: merchant.id,
        label: merchant.name,
      }));
      this.setState({ isLoading: false, merchants }, () => {
        if (merchants.length === 1) {
          this.setState({ selectedMerchant: merchants[0].value }, this.fetchBranches);
        }
      });
    }
  };

  fetchBranches = async () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant) return;

    const response = await this.engine.getItem(
      `merchants/${selectedMerchant}/branches`
    );
    if (response && response.status === 200) {
      const branches = response.data.items.map((branch) => ({
        value: branch.id,
        label: branch.name || branch.address,
      }));
      this.setState({ isLoading: false, branches }, () => {
        if (branches.length === 1) {
          this.setState({ selectedBranch: branches[0].value }, this.callPage);
        }
      });
    }
  };

  // one card as the table reads it; the card itself is kept on the row so
  // the detail dialog and the work order have everything they need
  toRow = (item, key) => ({
    id: key,
    data: item,
    jobCardId: item.id,
    bookingId: item.bookingId,
    plateNumber: item.plateNumber || "-",
    vehicle: [item.vehicleMake, item.vehicleModel, item.vehicleYear]
      .filter(Boolean)
      .join(" "),
    customerName: item.customerName || "-",
    customerPhone: item.customerPhone || "-",
    worker: item.workerName || "-",
    mileage: item.mileage,
    status: item.status,
    // the invoice lives on the card's work order: no work order and an open
    // quote both read as unpaid, since nothing has been collected either way
    workOrderId: item.workOrderId,
    paymentStatus: item.paymentStatus,
    photos: (item.photos || []).length,
    created: new Date(item.createdAt),
  });

  callPage = async () => {
    const { selectedBranch } = this.state;
    if (!selectedBranch) {
      this.setState({ isLoading: false });
      return;
    }

    this.setState({ isLoading: true });
    const response = await this.engine.getItem(
      `merchants-branches/${selectedBranch}/job-cards`
    );
    if (response && response.status === 200) {
      // a page of the cards still being worked; older builds of the api
      // answered with a plain array, so both shapes are read
      const items = Array.isArray(response.data)
        ? response.data
        : response.data.items || [];
      this.setState({ isLoading: false, dataSource: items.map(this.toRow) });
    } else {
      this.setState({ isLoading: false });
    }
    this.props.loadingAction(false);
  };

  /**
   * A page of this garage's closed cards, newest first.
   *
   * The history has no ceiling — a garage that has been open a year has
   * thousands of finished cards — so it is never loaded whole. The server
   * says only whether another page exists behind this one, which is all the
   * pager needs to offer "next".
   */
  fetchHistory = async (page = 0, pageSize = this.state.historyPageSize) => {
    const { selectedBranch } = this.state;
    if (!selectedBranch) return;

    this.setState({ historyLoading: true });
    try {
      const response = await this.engine.getItem(
        `merchants-branches/${selectedBranch}/job-cards` +
          `?scope=closed&limit=${pageSize}&offset=${page * pageSize}`
      );
      const data = (response && response.data) || {};
      const items = Array.isArray(data) ? data : data.items || [];
      this.setState({
        historyLoading: false,
        historyRows: items.map(this.toRow),
        historyPage: page,
        historyPageSize: pageSize,
        // a short page is the end of the history whatever the server says
        historyHasNext: Boolean(data.hasNext) && items.length === pageSize,
      });
    } catch (err) {
      this.setState({
        historyLoading: false,
        historyRows: [],
        historyHasNext: false,
      });
    }
  };

  openHistory = () => {
    this.setState({ historyOpen: true });
    this.fetchHistory(0);
  };

  // Sent here to look at one card (e.g. from a staff report): open it straight
  // away, closed or not, then drop the request so a reload does not repeat it.
  openJobCard = async (branchId, id) => {
    const { history, location } = this.props;
    history.replace({ ...location, state: { ...location.state, openJobCardId: undefined } });
    try {
      const res = await this.engine.getItem(
        `merchants-branches/${branchId}/job-cards/${id}`
      );
      if (res && res.data) this.setState({ viewing: res.data });
    } catch (e) {
      // the list is still there to pick from
    }
  };

  closeHistory = () => this.setState({ historyOpen: false });

  // Leaving the history to read one card, and back again on close: two
  // dialogs are never open at once.
  viewFromHistory = (row) =>
    this.setState({
      viewing: row.data,
      historyOpen: false,
      viewingFromHistory: true,
    });

  // A step taken from the card (inspectors, photos, closing) shows in the
  // rows behind it too.
  refreshAfterCardChange = () => {
    this.callPage();
    if (this.state.viewingFromHistory) this.fetchHistory(this.state.historyPage);
  };

  closeDetail = () =>
    this.setState((prev) => ({
      viewing: null,
      historyOpen: prev.viewingFromHistory,
      viewingFromHistory: false,
    }));

  handleBranchSelect = (event) => {
    const selectedBranch = event.value;
    const match = this.state.branches.find((b) => b.value === selectedBranch);
    this.setState(
      {
        selectedBranch,
        branchName: match ? match.label : "",
        dataSource: [],
        // another garage's history has nothing to do with this one
        historyRows: [],
        historyPage: 0,
        historyHasNext: false,
      },
      this.callPage
    );
  };

  renderStatus(status) {
    const label = strings[`job_card_${status}`] || status;
    return <span className={`job-card-status is-${status}`}>{label}</span>;
  }

  /**
   * Where the card's invoice stands, taken off its work order.
   *
   * Paid is a plain badge — there is nothing left to do to it. Anything else
   * is a button: the counter takes the money and says so from here, without
   * opening the work order to do it.
   */
  renderPayment(row) {
    const isPaid = row.paymentStatus === "paid";
    const label = isPaid ? strings.payment_paid : strings.payment_unpaid;

    if (isPaid) {
      return <span className="job-card-pay is-paid">{label}</span>;
    }

    return (
      <button
        type="button"
        className="job-card-pay is-unpaid"
        title={strings.mark_as_paid}
        onClick={() => this.askMarkPaid(row)}
      >
        <i className="fa fa-money" /> {label}
      </button>
    );
  }

  askMarkPaid = (row) => {
    // A card nobody has opened a work order against has no invoice to settle.
    if (!row.workOrderId) {
      alert(strings.no_work_order_to_settle);
      return;
    }
    this.setState({ payingRow: row });
  };

  closeMarkPaid = () => this.setState({ payingRow: null, settling: false });

  /**
   * Settles the invoice on one card. The api refuses this while the quote is
   * still open, so its own message is what the admin is shown when the row
   * does not move.
   */
  markPaid = async () => {
    const { payingRow, historyOpen } = this.state;
    if (!payingRow) return;

    this.setState({ settling: true });
    try {
      await this.engine.patchItem(
        `work-orders/${payingRow.workOrderId}/confirm-payment`
      );
      this.setState({ payingRow: null, settling: false });
      this.callPage();
      if (historyOpen) this.fetchHistory(this.state.historyPage);
    } catch (err) {
      this.setState({ payingRow: null, settling: false });
      alert(apiErrorMessage(err));
    }
  };

  /**
   * Opens the work order for a job card — booking-based cards resolve through
   * the booking, walk-ins through the job card itself.
   *
   * On the working list a card with no work order yet gets one, which is how
   * the job is picked up. `create` turns that off for the history: a closed
   * card is a record of work already finished, and opening a row there must
   * never start a fresh report against it. Where there is nothing to open it
   * says so instead.
   */
  handleViewWorkOrder = async (row, { create = true } = {}) => {
    this.props.loadingAction(true);
    const pathname = row.bookingId
      ? `bookings/${row.bookingId}/work-order`
      : `job-cards/${row.jobCardId}/work-order`;

    try {
      let response;
      try {
        response = await this.engine.getItem(pathname);
      } catch (err) {
        if (!create) {
          this.props.loadingAction(false);
          alert(strings.no_work_order_for_card);
          return;
        }
        response = await this.engine.saveItem(pathname, {});
      }
      if (response && response.data) {
        const workOrder = response.data;
        this.props.history.push({
          pathname: `/admin/work-orders/${workOrder.id}/manage`,
          state: {
            workOrder: { id: workOrder.id },
            booking: row.bookingId ? { id: row.bookingId } : null,
            meta: { customer: row.customerName, vehicle: row.vehicle },
          },
        });
      }
    } catch (err) {
      alert(apiErrorMessage(err));
    }
    this.props.loadingAction(false);
  };

  render() {
    const {
      dataSource,
      isLoading,
      merchants,
      branches,
      selectedMerchant,
      selectedBranch,
      branchName,
      garageScoped,
      viewing,
      historyOpen,
      historyRows,
      historyLoading,
      historyPage,
      historyPageSize,
      historyHasNext,
      payingRow,
      settling,
    } = this.state;

    const columns = [
      { Header: strings.platenumber, accessor: "plateNumber" },
      { Header: strings.vehicle, accessor: "vehicle" },
      { Header: strings.customer, accessor: "customerName" },
      { Header: strings.phone, accessor: "customerPhone" },
      { Header: strings.worker, accessor: "worker" },
      {
        Header: strings.arrivalmileage,
        accessor: "mileage",
        Cell: (row) =>
          row.value || row.value === 0 ? formatMileage(row.value) : "-",
      },
      {
        Header: strings.status,
        accessor: "status",
        Cell: (row) => this.renderStatus(row.value),
      },
      {
        Header: strings.payment,
        accessor: "paymentStatus",
        sortable: false,
        filterable: false,
        Cell: (row) => this.renderPayment(row.original),
      },
      {
        Header: strings.created,
        accessor: "created",
        Cell: (row) => row.value.toLocaleDateString(),
      },
      {
        Header: strings.actions,
        accessor: "id",
        sortable: false,
        filterable: false,
        Cell: (row) => (
          <RowActions
            actions={[
              {
                key: "work-order",
                icon: "fa fa-wrench",
                label: strings.work_order,
                // opens the card itself; the work order is one step on from
                // there, alongside sending to inspect and closing the card
                primary: true,
                onClick: () => this.setState({ viewing: row.original.data }),
              },
            ]}
          />
        ),
      },
    ];

    // The history is read, not worked: the status column goes, since every
    // card in here is closed, and the work order opens as it was left rather
    // than being created on the way in.
    const historyColumns = columns
      .filter((column) => column.accessor !== "status")
      .map((column) =>
        column.accessor === "id"
          ? {
              ...column,
              Cell: (row) => (
                <RowActions
                  actions={[
                    {
                      key: "view",
                      icon: "fa fa-eye",
                      label: strings.open,
                      onClick: () => this.viewFromHistory(row.original),
                    },
                    {
                      key: "work-order",
                      icon: "fa fa-wrench",
                      label: strings.work_orders,
                      // the report as it was left; never a new one
                      onClick: () =>
                        this.handleViewWorkOrder(row.original, {
                          create: false,
                        }),
                    },
                  ]}
                />
              ),
            }
          : column
      );

    return (
      <div className="content">
        <GarageSectionHeader
          branchId={garageScoped ? selectedBranch : ""}
          branchName={branchName}
          merchantId={selectedMerchant}
          active="job-cards"
          history={this.props.history}
        />

        <PageHeader
          icon="nc-icon nc-paper"
          title={strings.job_cards}
          subtitle={strings.job_cards_subtitle}
          count={dataSource.length > 0 ? dataSource.length : undefined}
        >
          {!garageScoped && !sessionStorage.getItem("merchantId") &&
            merchants.length !== 1 && (
              <div className="page-head__select">
                <SelectRow
                  hidelabel
                  name="selectedMerchant"
                  defaultValue={selectedMerchant}
                  data={merchants}
                  changeInput={(e) =>
                    this.setState(
                      { selectedMerchant: e.value, dataSource: [] },
                      this.fetchBranches
                    )
                  }
                  placeholder={strings.merchant}
                />
              </div>
            )}
          {!garageScoped && (
            <div className="page-head__select">
              <SelectRow
                hidelabel
                key={selectedMerchant || "default"}
                name="selectedBranch"
                defaultValue={selectedBranch}
                data={branches}
                changeInput={this.handleBranchSelect}
                placeholder={strings.garage}
              />
            </div>
          )}
          <Button
            color="default"
            disabled={!selectedBranch}
            onClick={this.openHistory}
          >
            <i className="fa fa-history" /> {strings.job_cards_history}
          </Button>
        </PageHeader>

        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                {!isLoading && !selectedBranch ? (
                  <EmptyState
                    icon="nc-icon nc-paper"
                    title={strings.select_garage_first}
                  />
                ) : (
                  <ReactTable
                    previousText={strings.previous}
                    nextText={strings.next}
                    loadingText={strings.loading}
                    noDataText={strings.no_job_cards}
                    pageText={strings.page}
                    data={dataSource}
                    defaultFilterMethod={this.filterCaseInsensitive}
                    filterable
                    loading={isLoading}
                    columns={columns}
                    defaultPageSize={10}
                    showPaginationTop={false}
                    showPaginationBottom
                    PaginationComponent={TablePagination}
                    className="-striped -highlight primary-pagination"
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <FormModal
          isOpen={historyOpen}
          onClose={this.closeHistory}
          icon="fa fa-history"
          title={strings.job_cards_history}
          subtitle={branchName || strings.job_cards_history_subtitle}
        >
          <div className="job-card-history">
            <ReactTable
              previousText={strings.previous}
              nextText={strings.next}
              loadingText={strings.loading}
              noDataText={strings.no_closed_job_cards}
              pageText={strings.page}
              data={historyRows}
              loading={historyLoading}
              columns={historyColumns}
              /* one page at a time comes from the server, so react-table is
                 told not to slice or count the rows itself */
              manual
              serverPaged
              page={historyPage}
              pageSize={historyPageSize}
              pages={historyHasNext ? historyPage + 2 : historyPage + 1}
              onPageChange={(page) => this.fetchHistory(page)}
              onPageSizeChange={(size) => this.fetchHistory(0, size)}
              showPaginationTop={false}
              showPaginationBottom
              PaginationComponent={TablePagination}
              className="-striped -highlight primary-pagination"
            />
          </div>

          <div className="mform__footer">
            <button
              type="button"
              className="btn btn-default"
              onClick={this.closeHistory}
            >
              {strings.close}
            </button>
          </div>
        </FormModal>

        <FormModal
          isOpen={Boolean(viewing)}
          onClose={this.closeDetail}
          icon="nc-icon nc-paper"
          title={strings.job_card}
          subtitle={viewing ? viewing.plateNumber : ""}
        >
          {viewing && (
            <JobCardView
              key={viewing.id}
              jobCard={viewing}
              branchId={selectedBranch}
              serverLink={Constants.serverlink}
              onClose={this.closeDetail}
              onChanged={this.refreshAfterCardChange}
              // a closed card opens its report as it was left, never a new one
              onOpenWorkOrder={(card) =>
                this.handleViewWorkOrder(this.toRow(card, card.id), {
                  create: card.status !== "closed",
                })
              }
            />
          )}
        </FormModal>

        <ConfirmModal
          isOpen={Boolean(payingRow)}
          title={strings.mark_as_paid}
          body={strings.formatString(
            strings.confirm_mark_paid_body,
            payingRow ? payingRow.plateNumber : ""
          )}
          confirmLabel={strings.confirm}
          color="success"
          saving={settling}
          onConfirm={this.markPaid}
          onClose={this.closeMarkPaid}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(JobCardList);
